import React, { createContext, useContext, useMemo } from "react";
import { FirmAdminDataApi } from "@cygnusaiinternal/cygnus-common";
import {
  getAddUserFormSchema,
  getRoleFeaturesMatrix,
  getAllFeatures,
  getUserRoles,
  getAllRoles,
  addUser,
  updateUserRole,
  updateUserStatus,
  updateRoleFeatures,
  getProducts,
  getAvailablePackages,
  getCurrentPackage,
  getUserProductAccess,
  getFirmSpecialPrivilegeRules,
  updateUserAccess,
  updateUserProductAccess,
  updateUserProductRole,
  updateUserProductFeatures,
  getUserAuditTrail,
} from "../pages/FirmAdmin/FirmAdmin.service";

const getFirmId = (): number =>
  parseInt(
    localStorage.getItem("FirmId") ?? localStorage.getItem("firmId") ?? "0",
    10
  );

const FirmAdminContext = createContext<FirmAdminDataApi | null>(null);

interface FirmAdminProviderProps {
  children: React.ReactNode;
}

export const FirmAdminProvider: React.FC<FirmAdminProviderProps> = ({ children }) => {
  const api = useMemo<FirmAdminDataApi>(
    () => ({
      getAddUserFormSchema: async () => (await getAddUserFormSchema()) ?? [],
      getRoleFeaturesMatrix: async () => getRoleFeaturesMatrix(getFirmId()),
      getAllFeatures: async () => (await getAllFeatures(getFirmId())) ?? [],
      getUserRoles: async () => (await getUserRoles()) ?? [],
      getAllRoles: async () => (await getAllRoles(getFirmId())) ?? [],
      addUser: async (data) => addUser(data),
      updateUserRole: async (userName, role) => updateUserRole(userName, role),
      updateUserStatus: async (userName, status) => updateUserStatus(userName, status),
      updateFirmUserStatus: async (userName, status) => updateUserStatus(userName, status),
      updateRoleFeatures: async (roleName, featureCodes, productCode, roleId) =>
        updateRoleFeatures(roleName, featureCodes, productCode, roleId),
      getProducts: async () => (await getProducts()) ?? [],
      getAvailablePackages: async () => (await getAvailablePackages(getFirmId())) ?? [],
      getCurrentPackage: async () => getCurrentPackage(getFirmId()),
      getUserProductAccess: async () => (await getUserProductAccess(getFirmId())) ?? [],
      getFirmSpecialPrivilegeRules: async () => getFirmSpecialPrivilegeRules(),
      updateUserAccess: async (payload) => updateUserAccess(payload),
      updateUserProductAccess: async (userName, productCode, enabled) =>
        updateUserProductAccess(getFirmId(), userName, productCode, enabled),
      updateUserProductRole: async (userName: string, productCode: string, roleId: number) =>
        updateUserProductRole(getFirmId(), userName, productCode, roleId),
      updateUserProductFeatures: async (userName: string, productCode: string, featureIds: number[]) =>
        updateUserProductFeatures(getFirmId(), userName, productCode, featureIds),
      getUserAuditTrail: async (params:any) => getUserAuditTrail(params),
    }),
    [],
  );

  return (
    <FirmAdminContext.Provider value={api}>{children}</FirmAdminContext.Provider>
  );
};

export const useFirmAdminApi = (): FirmAdminDataApi => {
  const context = useContext(FirmAdminContext);

  if (!context) {
    throw new Error("useFirmAdminApi must be used within a FirmAdminProvider");
  }

  return context;
};
